import React, { useEffect, useState } from "react";
import useTranslationCustom from "../../i18n/customHook/useTranslation";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { getLoginConfig } from "../../redux/slices/loginconfigSlice";
import { setActivityIndicator } from "../../redux/slices/appconfigSlice";
import "./style.scss";
import AppConfig from "../../helpers/config";
import { GetStaticPageContent } from "../../services/StaticContentService";

const CookieConsent = ({ onConsentGiven }) => {
    const { T } = useTranslationCustom();
    const dispatch = useDispatch();
    const [visible, setVisible] = useState(false);
    const [cookiePolicy, setCookiePolicy] = useState(false);
    const [htmlContent, setHtmlContent] = useState('');
    const loginConfig = useSelector(getLoginConfig);

    // Resolves once the GA client_id has been stored, so callers can rely on
    // GA_COOKIE_ID being present. gtag's `get` callback is async and never fires
    // when GA is blocked, so we cap the wait.
    const grantConsent = () => new Promise((resolve) => {
        if (typeof window.gtag !== 'function') return resolve();

        window.gtag('consent', 'update', {
            'ad_storage': 'granted',
            'ad_user_data': 'granted',
            'ad_personalization': 'granted',
            'analytics_storage': 'granted'
        });

        let settled = false;
        const done = () => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve();
        };
        const timer = setTimeout(done, 3000);

        window.gtag('get', AppConfig.gaMeasurementID, 'client_id', (id) => {
            if (id) localStorage.setItem('GA_COOKIE_ID', id);
            done();
        });
    });

    useEffect(() => {
        getStaticContent(loginConfig?.locale);
        const consent = localStorage.getItem("cookie_consent");
        if (!consent) setVisible(true);
        else if (consent === "accepted") grantConsent();
    }, []);

    const getStaticContent = async (language) => {
        try {
            dispatch(setActivityIndicator(true));
            // Goes through HttpClient so the auth token, loading state and
            // cancellation are handled by the shared interceptors.
            setHtmlContent(await GetStaticPageContent("cookie_policy", language));
            dispatch(setActivityIndicator(false));
        } catch (e) {
            console.error('static page error', e);
            dispatch(setActivityIndicator(false));
        }
    }

    const acceptCookies = async () => {
        localStorage.setItem("cookie_consent", "accepted");
        // Must await: downstream get-transaction-id reads GA_COOKIE_ID immediately.
        await grantConsent();
        setVisible(false);
        onConsentGiven();
    };

    const declineCookies = () => {
        localStorage.setItem("cookie_consent", "declined");
        setVisible(false);
        onConsentGiven();
    };

    useEffect(() => {
        document.body.style.overflow = visible ? "hidden" : "";
        // Without this cleanup, unmounting while the banner is still up leaves
        // the page permanently unscrollable.
        return () => {
            document.body.style.overflow = "";
        };
    }, [visible]);

    if (!visible) return null;

    const styleLowerPopup = 'position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-end';
    const styleLargePopup = 'position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center cookies-expanded'
    const buttonsPositions = 'position-fixed bottom-0 end-30 d-flex justify-content-end gap-3 mt-4 bottom-btns';

    return (
        <div
            className={cookiePolicy ? styleLargePopup : styleLowerPopup}
            style={{ backgroundColor: "rgba(0, 0, 0, 0.6)", zIndex: 1050, backdropFilter: "blur(4px)" }}
        >
            <div
                className="bg-white rounded-3 shadow-xl p-4 p-md-5 m-3 animate__animated animate__fadeInUp"
                style={{
                    // "90hv" was not a valid CSS unit, so the expanded policy
                    // view silently fell back to no max-width at all.
                    maxWidth: cookiePolicy ? "90vw" : "600px",
                    width: "100%",
                    border: "1px solid rgba(0, 0, 0, 0.05)"
                }}
            >
                <div className="d-flex align-items-center mb-3">
                    <span className="display-6 me-2">🍪</span>
                    <h4 className="fw-bold mb-0 text-dark">{cookiePolicy ? htmlContent?.title : T("Profile:Consent_Heading")}</h4>
                </div>
                {cookiePolicy ?
                    <div
                        className="text-secondary mb-4 fs-6"
                        dangerouslySetInnerHTML={{ __html: htmlContent?.content }}
                    />
                    : <p className="text-secondary mb-4 fs-6">
                        {T("Profile:Consent_Description")}{" "}
                        <a onClick={() => setCookiePolicy(!cookiePolicy)}
                            className="text-primary text-decoration-none fw-medium">
                            {T("Profile:Button_Cookie_Policy")}
                        </a>.
                    </p>}

                <div
                    className={`${cookiePolicy ? buttonsPositions : "d-flex flex-column flex-md-row justify-content-end gap-3 cookies-btns"}`}>
                    <button
                        onClick={declineCookies}
                        className="btn btn-outline-dark"
                        style={{ transition: "all 0.3s ease" }}
                    >
                        {T("Profile:Button_Decline")}
                    </button>
                    <button
                        onClick={acceptCookies}
                        className="btn btn-primary"
                        style={{ transition: "all 0.3s ease", backgroundColor: "#0d6efd", border: "none" }}
                    >
                        {T("Profile:Button_Accept_All")}
                    </button>
                </div>

                <small className="d-block mt-4 text-center text-muted fs-6">
                    {/* © {new Date().getFullYear()} {T("Profile:Bottom_Copy_Right_Text")} */}
                    © Genius®
                </small>
            </div>
        </div>
    );
};

CookieConsent.propTypes = {
    onConsentGiven: PropTypes.func,
}

export default CookieConsent;
